import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const sections = [
  {
    title: '트레이너 앱 가입 & 로그인',
    content: (
      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
        <p className="font-medium">초대링크로 가입하기 (신규)</p>
        <div className="space-y-1.5 ml-2">
          <p>1. 헬스장 관리자에게 초대링크를 받으세요.</p>
          <p>2. 링크 접속 후 이메일과 비밀번호를 설정하면 가입이 완료됩니다.</p>
          <p>3. 이후 로그인 화면에서 이메일 + 비밀번호로 로그인하세요.</p>
        </div>
        <p className="font-medium mt-3">매장코드 + 전화번호로 로그인하기 (기존 방식)</p>
        <div className="space-y-1.5 ml-2">
          <p>1. 로그인 화면 하단 '트레이너이신가요?' 버튼을 누르세요.</p>
          <p>2. 헬스장 매장 코드와 등록된 전화번호를 입력하면 로그인됩니다.</p>
        </div>
        <p className="text-xs text-gray-400 mt-2">매장 코드는 헬스장 관리자에게 문의하세요.</p>
      </div>
    ),
  },
  {
    title: 'PT 수업 기록',
    content: (
      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
        <p>1. 'PT 현황' 탭에서 담당 회원 카드를 찾으세요.</p>
        <p>2. '수업 기록' 버튼을 눌러 날짜, 운동 내용, 메모를 입력하세요.</p>
        <p>3. 저장하면 잔여 횟수가 자동 차감됩니다.</p>
        <div className="mt-2 p-2.5 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
          <p className="text-xs text-yellow-700 dark:text-yellow-400">잔여 횟수가 0이 되면 수업 기록 버튼이 비활성화됩니다. 관리자에게 PT 재등록을 요청하세요.</p>
        </div>
      </div>
    ),
  },
  {
    title: 'PT 일정 예약',
    content: (
      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
        <p>1. 'PT 일정' 탭에서 '+ 일정 등록' 버튼을 누르세요.</p>
        <p>2. 회원을 선택하고 날짜와 시간을 설정한 후 저장합니다.</p>
        <p>3. 등록된 일정은 회원 앱에서도 확인할 수 있습니다.</p>
        <p className="mt-1">예약된 일정은 완료 또는 취소 처리할 수 있습니다.</p>
      </div>
    ),
  },
  {
    title: '신체 정보 기록',
    content: (
      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
        <p>PT 현황 탭에서 회원 카드 → '신체 기록' 버튼을 눌러 체중, 체지방, 근육량 등을 입력할 수 있습니다.</p>
        <p>기록된 신체 정보는 회원 앱 '내 신체 정보' 탭에서 그래프로 확인할 수 있습니다.</p>
      </div>
    ),
  },
  {
    title: '자주 묻는 질문',
    content: (
      <div className="space-y-3">
        <div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Q. 과거 PT 일지를 수정하거나 삭제할 수 있나요?</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">→ 트레이너 앱에서는 조회만 가능합니다. 수정·삭제가 필요하면 헬스장 관리자에게 요청하세요.</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Q. 비밀번호를 잊어버렸어요.</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">→ 로그인 화면 하단 '비밀번호를 잊으셨나요?'에서 이메일로 재설정 링크를 받으세요.</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Q. 내가 담당하지 않는 회원이 보여요.</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">→ PT 현황에는 본인에게 배정된 PT가 있는 회원만 표시됩니다. 헬스장 관리자에게 PT 배정을 확인하세요.</p>
        </div>
      </div>
    ),
  },
];

export default function TrainerHelp() {
  const [open, setOpen] = useState(0);

  return (
    <div className="p-4 space-y-3">
      <div className="mb-2">
        <h2 className="font-semibold text-gray-900 dark:text-white">도움말</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">자주 묻는 질문</p>
      </div>
      {sections.map((section, i) => (
        <div key={i} className="card overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3.5 text-left"
            onClick={() => setOpen(open === i ? null : i)}
          >
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200 pr-4">
              {section.title}
            </span>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${open === i ? 'rotate-180' : ''}`}
            />
          </button>
          {open === i && (
            <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-3 leading-relaxed">
              {section.content}
            </div>
          )}
        </div>
      ))}

      <div className="card p-4 text-center mt-4">
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">추가 문의</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">헬스장 관리자에게 직접 문의하거나</p>
        <a
          href="mailto:ezbiz.manager@gmail.com"
          className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          ezbiz.manager@gmail.com
        </a>
      </div>
    </div>
  );
}
